// ✅ src/pages/VehicleV2/HireVehicleBillApprovalEntry.jsx  (UPDATED FULL FILE)
// Pattern updated based on HireVehicleBillApprovalHOListing.jsx
//
// Assumed backend mount:
//   app.use("/api/vehicleBillApproval", vehicleBillApproval);
//
// Backend endpoint used:
//   GET /api/vehicleBillApproval/details?billId=
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + detailLoading states
// - cleaner fetch / refresh handling
// - consistent card / section / button style
// - safer detail mapping + demo fallback
// - API base aligned with listing file

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FaArrowLeft, FaPrint, FaSyncAlt } from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/vehicleBillApprovalEntry";

/* ---------------- helpers ---------------- */
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, d = "") => {
  if (v === null || v === undefined) return d;
  return String(v).trim();
};

const toMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
};

const StatCell = ({ label, value, isDark }) => (
  <div className="flex flex-col">
    <div
      className={`text-xs font-semibold ${
        isDark ? "text-gray-300" : "text-gray-600"
      }`}
    >
      {label}
    </div>
    <div
      className={`text-sm font-bold ${
        isDark ? "text-white" : "text-gray-900"
      }`}
    >
      {value}
    </div>
  </div>
);

const SectionTitle = ({ title, isDark }) => (
  <div
    className={`px-4 py-2 font-bold uppercase text-xs tracking-wider border-y ${
      isDark
        ? "bg-gray-800 border-gray-700 text-blue-200"
        : "bg-blue-50 border-blue-100 text-blue-900"
    }`}
  >
    {title}
  </div>
);

const HireVehicleBillApprovalEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const navigate = useNavigate();
  const location = useLocation();

  const qs = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const billId = toInt(qs.get("billId"), 0);

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdFromStorage =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const defaultBranchId = toInt(branchIdFromStorage, 0);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdFromStorage,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchIdFromStorage, branchType, userId]
  );

  /* ---------------- state ---------------- */
  const [pageLoading, setPageLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [header, setHeader] = useState({
    billNo: "",
    partyName: "",
    vehicleName: "",
    vehicleType: "",
    vehicleRegNo: "",
    panCardNo: "",
    mailingAddress: "",
    vehicleBasis: "",
    vehicle: "",
    billDate: "",
  });

  const [summary, setSummary] = useState({
    particulars: "",
    month: "",
    ratePerHour: "0.00",
    totalAmount: "0.00",

    totalHr: "0.00",
    totalKm: "0.00",
    breakingHours: "0.00",

    betaOthers: "0.00",
    advance: "0.00",
    totalProgressAmount: "0.00",
    breakingHoursAmount: "0.00",
    grandTotalAmount: "0.00",

    sparesDeduction: "0.00",
    tdsDeduction: "0.00",
    materialTotalAmount: "0.00",

    maintenanceReason: "",
    maintenanceCharges: "0.00",

    otherCharges: "0.00",
    balancePayable: "0.00",

    preparedBy: "",
  });

  const isBusy = pageLoading || detailLoading;

  /* ---------------- demo fallback ---------------- */
  const demoData = useMemo(
    () => ({
      header: {
        billNo: "BILL/007/0006",
        partyName: "",
        vehicleName: "TIPPER 10W",
        vehicleType: "Tipper 10 Wheeler",
        vehicleRegNo: "MZ01L1303",
        panCardNo: "",
        mailingAddress: "",
        vehicleBasis: "KM",
        vehicle: "",
        billDate: "04/12/2018",
      },
      summary: {
        particulars: "",
        month: "",
        ratePerHour: "0.00",
        totalAmount: "0.00",
        totalHr: "0.00",
        totalKm: "10.00",
        breakingHours: "0.00",
        betaOthers: "0.00",
        advance: "0.00",
        totalProgressAmount: "0.00",
        breakingHoursAmount: "0.00",
        grandTotalAmount: "2000.00",
        sparesDeduction: "0.00",
        tdsDeduction: "20.00",
        materialTotalAmount: "0.00",
        maintenanceReason: "",
        maintenanceCharges: "0.00",
        otherCharges: "0.00",
        balancePayable: "2000.00",
        preparedBy: "",
      },
    }),
    []
  );

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const softBox = useMemo(
    () =>
      `rounded-xl border ${
        isDark ? "bg-gray-800/60 border-gray-700" : "bg-white border-gray-200"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `text-xs font-semibold ${isDark ? "text-gray-300" : "text-gray-600"}`,
    [isDark]
  );

  const valueClass = useMemo(
    () => `text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`,
    [isDark]
  );

  /* ---------------- helpers ---------------- */
  const applyDetails = useCallback((data) => {
    const h = data?.header || {};
    const s = data?.summary || {};

    setHeader({
      billNo: toStr(h.billNo ?? h.fld_billno),
      partyName: toStr(h.partyName ?? h.fld_partyname ?? h.fld_hiredfrom),
      vehicleName: toStr(h.vehicleName ?? h.fld_vehicle_name),
      vehicleType: toStr(
        h.vehicleType ?? h.fld_vehicle_subtypename ?? h.fld_vehicletypename
      ),
      vehicleRegNo: toStr(h.vehicleRegNo ?? h.fld_vehicle_regno),
      panCardNo: toStr(h.panCardNo ?? h.fld_pan_cardno),
      mailingAddress: toStr(h.mailingAddress ?? h.fld_mailingaddress),
      vehicleBasis: toStr(h.vehicleBasis ?? h.fld_vehicle_basistype),
      vehicle: toStr(h.vehicle ?? h.fld_vehicle_code),
      billDate: toStr(h.billDate ?? h.fld_billdate),
    });

    setSummary({
      particulars: toStr(s.particulars ?? s.fld_particulars),
      month: toStr(s.month ?? s.fld_month),
      ratePerHour: toMoney(s.ratePerHour ?? s.fld_rateperhour ?? 0),
      totalAmount: toMoney(s.totalAmount ?? s.fld_total_amount ?? 0),

      totalHr: toMoney(s.totalHr ?? s.fld_total_hr ?? s.fld_total_km_hr ?? 0),
      totalKm: toMoney(s.totalKm ?? s.fld_total_km ?? 0),
      breakingHours: toMoney(s.breakingHours ?? s.fld_breaking_hrs ?? 0),

      betaOthers: toMoney(s.betaOthers ?? s.fld_bata ?? 0),
      advance: toMoney(s.advance ?? s.fld_advance ?? 0),
      totalProgressAmount: toMoney(
        s.totalProgressAmount ?? s.fld_total_progress_amnt ?? 0
      ),
      breakingHoursAmount: toMoney(
        s.breakingHoursAmount ?? s.fld_breaking_amnt ?? 0
      ),
      grandTotalAmount: toMoney(s.grandTotalAmount ?? s.fld_grand_total ?? 0),

      sparesDeduction: toMoney(
        s.sparesDeduction ?? s.fld_deducted_spares_amnt ?? 0
      ),
      tdsDeduction: toMoney(s.tdsDeduction ?? s.fld_tds_amnt ?? 0),
      materialTotalAmount: toMoney(
        s.materialTotalAmount ?? s.fld_total_spares_amnt ?? 0
      ),

      maintenanceReason: toStr(
        s.maintenanceReason ?? s.fld_maintenance_remarks
      ),
      maintenanceCharges: toMoney(
        s.maintenanceCharges ?? s.fld_maintenance_amnt ?? 0
      ),

      otherCharges: toMoney(
        s.otherCharges ?? s.fld_other_deduction_amnt ?? 0
      ),
      balancePayable: toMoney(s.balancePayable ?? s.fld_balance_payable ?? 0),

      preparedBy: toStr(s.preparedBy ?? s.fld_username ?? s.fld_preparedby),
    });
  }, []);

  const applyDemo = useCallback(() => {
    applyDetails(demoData);
  }, [applyDetails, demoData]);

  /* ---------------- api ---------------- */
  const loadDetails = useCallback(async () => {
    if (!billId) {
      applyDemo();
      return;
    }

    try {
      setDetailLoading(true);

      const { data } = await axios.get(`${API_BASE}/details`, {
        params: {
          billId,
          loginUserId,
          branchId: defaultBranchId || 0,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Details load failed");
      }

      applyDetails(data);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load details"
      );
      applyDemo();
    } finally {
      setDetailLoading(false);
    }
  }, [
    billId,
    loginUserId,
    defaultBranchId,
    commonHeaders,
    applyDetails,
    applyDemo,
  ]);

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);
      await loadDetails();
    } finally {
      setPageLoading(false);
    }
  }, [loadDetails]);

  /* ---------------- handlers ---------------- */
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleRefresh = useCallback(async () => {
    await loadInit();
  }, [loadInit]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              HIRE VEHICLE BILL APPROVAL
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Hire Vehicle Bill Approval Entry
              </div>

              <div
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Branch ID: {defaultBranchId || "-"} {" • "} User ID:{" "}
                {loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>
                  {billId ? `Bill ID: ${billId}` : "Preview Mode"}
                </span>
                <span className={pillClass}>
                  Branch Type: {branchType || "-"}
                </span>
                <span className={pillClass}>
                  Status: {isBusy ? "Loading" : "Ready"}
                </span>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
                title="Refresh"
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handlePrint}
                disabled={isBusy}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-60`}
                type="button"
              >
                <FaPrint />
                Print
              </button>
            </div>
          </div>

          {/* Top info block */}
          <div className={`${softBox} overflow-hidden`}>
            <div className="px-4 py-3 text-center">
              <div
                className={`text-xs font-extrabold tracking-widest ${
                  isDark ? "text-blue-200" : "text-blue-900"
                }`}
              >
                HIRE VEHICLE BILL APPROVAL
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 pb-4">
              {/* Left */}
              <div className="space-y-2">
                <div className="flex gap-3">
                  <div className="w-40">
                    <div className={labelClass}>Bill No</div>
                  </div>
                  <div className={valueClass}>{header.billNo || "-"}</div>
                </div>

                <div className="flex gap-3">
                  <div className="w-40">
                    <div className={labelClass}>Name of the Party</div>
                  </div>
                  <div className={valueClass}>{header.partyName || "-"}</div>
                </div>

                <div className="flex gap-3">
                  <div className="w-40">
                    <div className={labelClass}>Vehicle Name</div>
                  </div>
                  <div className={valueClass}>{header.vehicleName || "-"}</div>
                </div>

                <div className="flex gap-3">
                  <div className="w-40">
                    <div className={labelClass}>Vehicle Type</div>
                  </div>
                  <div className={valueClass}>{header.vehicleType || "-"}</div>
                </div>

                <div className="flex gap-3">
                  <div className="w-40">
                    <div className={labelClass}>Vehicle RegNo</div>
                  </div>
                  <div className={valueClass}>{header.vehicleRegNo || "-"}</div>
                </div>
              </div>

              {/* Right */}
              <div className="space-y-2">
                <div className="flex gap-3">
                  <div className="w-40">
                    <div className={labelClass}>PAN Card No</div>
                  </div>
                  <div className={valueClass}>{header.panCardNo || "-"}</div>
                </div>

                <div className="flex gap-3">
                  <div className="w-40">
                    <div className={labelClass}>Mailing Address</div>
                  </div>
                  <div className={`${valueClass} break-words`}>
                    {header.mailingAddress || "-"}
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-40">
                    <div className={labelClass}>Vehicle Basis</div>
                  </div>
                  <div className={valueClass}>{header.vehicleBasis || "-"}</div>
                </div>

                <div className="flex gap-3">
                  <div className="w-40">
                    <div className={labelClass}>Vehicle</div>
                  </div>
                  <div className={valueClass}>{header.vehicle || "-"}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Main details */}
          <div className={`${softBox} mt-5 overflow-hidden`}>
            <div className="overflow-auto">
              <table className="min-w-[900px] w-full table-auto">
                <thead className={isDark ? "bg-gray-800" : "bg-gray-100"}>
                  <tr>
                    <th className="px-4 py-3 text-xs font-extrabold text-center whitespace-nowrap">
                      Particulars
                    </th>
                    <th className="px-4 py-3 text-xs font-extrabold text-center whitespace-nowrap">
                      Month
                    </th>
                    <th className="px-4 py-3 text-xs font-extrabold text-center whitespace-nowrap">
                      Rate per Hour
                    </th>
                    <th className="px-4 py-3 text-xs font-extrabold text-center whitespace-nowrap">
                      Total Amount
                    </th>
                  </tr>
                </thead>

                <tbody className={isDark ? "bg-gray-900" : "bg-white"}>
                  <tr
                    className={`border-t ${
                      isDark ? "border-gray-800" : "border-gray-100"
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className={labelClass}>Bill Date</div>
                      <div className={valueClass}>{header.billDate || "-"}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {summary.month || "-"}
                    </td>
                    <td className="px-4 py-3 text-right font-bold">
                      {summary.ratePerHour}
                    </td>
                    <td className="px-4 py-3 text-right font-bold">
                      {summary.totalAmount}
                    </td>
                  </tr>

                  <tr
                    className={`border-t ${
                      isDark ? "border-gray-800" : "border-gray-100"
                    }`}
                  >
                    <td colSpan={2} className="px-4 py-4">
                      <div className="grid grid-cols-3 gap-6">
                        <StatCell
                          label="Total Hr"
                          value={summary.totalHr}
                          isDark={isDark}
                        />
                        <StatCell
                          label="Total KM"
                          value={summary.totalKm}
                          isDark={isDark}
                        />
                        <StatCell
                          label="Breaking Hours"
                          value={summary.breakingHours}
                          isDark={isDark}
                        />
                      </div>
                    </td>

                    <td colSpan={2} className="px-4 py-4">
                      <div className="grid grid-cols-1 gap-2">
                        <div className="flex items-center justify-between">
                          <span className={labelClass}>Beta (Others)</span>
                          <span className={valueClass}>
                            {summary.betaOthers}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={labelClass}>Advance</span>
                          <span className={valueClass}>{summary.advance}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={labelClass}>Total Amount</span>
                          <span className={valueClass}>
                            {summary.totalProgressAmount}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={labelClass}>
                            Breaking Hours Amount
                          </span>
                          <span className={valueClass}>
                            {summary.breakingHoursAmount}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={`${labelClass} uppercase`}>
                            Total Amount
                          </span>
                          <span className={`${valueClass} text-base`}>
                            {summary.grandTotalAmount}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Material Deductions */}
            <SectionTitle title="Material Deductions" isDark={isDark} />
            <div className="px-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-3xl ml-auto">
                <div className="flex items-center justify-between">
                  <span className={labelClass}>Spares Deductions</span>
                  <span className={valueClass}>{summary.sparesDeduction}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={labelClass}>TDS Deductions</span>
                  <span className={valueClass}>{summary.tdsDeduction}</span>
                </div>
                <div className="flex items-center justify-between md:col-span-2">
                  <span className={`${labelClass} uppercase`}>
                    Total Amount
                  </span>
                  <span className={valueClass}>
                    {summary.materialTotalAmount}
                  </span>
                </div>
              </div>
            </div>

            {/* Maintenance Reason */}
            <SectionTitle title="Maintenance Reason" isDark={isDark} />
            <div className="px-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className={labelClass}>Maintenance Reason</div>
                  <div className={valueClass}>
                    {summary.maintenanceReason || "-"}
                  </div>
                </div>
                <div className="text-right">
                  <div className={labelClass}>Maintenance Charges</div>
                  <div className={valueClass}>
                    {summary.maintenanceCharges}
                  </div>
                </div>
              </div>
            </div>

            {/* Deduction */}
            <SectionTitle title="Deduction" isDark={isDark} />
            <div className="px-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-3xl ml-auto">
                <div className="flex items-center justify-between">
                  <span className={labelClass}>Other Charges</span>
                  <span className={valueClass}>{summary.otherCharges}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`${labelClass} uppercase`}>
                    Balance Payable
                  </span>
                  <span className={`${valueClass} text-base`}>
                    {summary.balancePayable}
                  </span>
                </div>
              </div>
            </div>

            {/* Prepared by */}
            <div className="px-4 pb-6">
              <div className={labelClass}>Prepared by</div>
              <div className={valueClass}>{summary.preparedBy || "-"}</div>
            </div>
          </div>

          {/* Loading text */}
          {isBusy && (
            <div
              className={`mt-3 text-sm ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {pageLoading
                ? "Loading page..."
                : detailLoading
                ? "Loading details..."
                : "Loading..."}
            </div>
          )}

          {/* Back */}
          <div className="flex justify-start mt-6">
            <button
              onClick={handleBack}
              disabled={isBusy}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-700"
              } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              <FaArrowLeft />
              Back
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default HireVehicleBillApprovalEntry;